import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Calendar, X } from 'lucide-react';
import { useEventsByMonth } from '../hooks/useEvents';
import { useCreateEvent } from '../hooks/useEventMutations';
import { EventCalendar, EventListItem } from './EventCalendar';
import { EventForm } from './EventForm';
import type { Event, EventFormValues } from '../types';
import { EVENT_TYPE_LABELS } from '../types';

// ── Component ─────────────────────────────────────────────────────────────────

export function EventsPage() {
  const navigate = useNavigate();

  // Calendar navigation
  const today      = new Date();
  const [year, setYear]   = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());

  // Drawer / modal state
  const [showForm, setShowForm]       = useState(false);
  const [selectedDay, setSelectedDay] = useState<{ date: Date; events: Event[] } | null>(null);

  const { data: events = [], isLoading } = useEventsByMonth(year, month);
  const createEvent = useCreateEvent();

  // ── Handlers ───────────────────────────────────────────────────────────────

  function prevMonth() {
    if (month === 0) { setMonth(11); setYear((y) => y - 1); }
    else setMonth((m) => m - 1);
  }

  function nextMonth() {
    if (month === 11) { setMonth(0); setYear((y) => y + 1); }
    else setMonth((m) => m + 1);
  }

  function handleDayClick(date: Date, dayEvents: Event[]) {
    setSelectedDay({ date, events: dayEvents });
  }

  async function handleCreate(values: EventFormValues) {
    await createEvent.mutateAsync(values);
    setShowForm(false);
  }

  // ── Stats strip ────────────────────────────────────────────────────────────

  const thisMonthEvents = events.filter((e) => {
    const d = new Date(e.starts_at);
    return d.getMonth() === month && d.getFullYear() === year;
  });

  const byType = Object.entries(EVENT_TYPE_LABELS).map(([type, label]) => ({
    type, label,
    count: thisMonthEvents.filter((e) => e.type === type).length,
  })).filter((s) => s.count > 0);

  return (
    <div className="space-y-8 pb-12 animate-fade-in">

      {/* ── Header ── */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold text-ink">Agenda</h1>
          <p className="text-sm text-ink-muted mt-1">
            Gerencie eventos, reuniões e atividades do tenant.
          </p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center gap-2 rounded-xl btn-accent px-4 py-2.5 text-sm font-semibold"
        >
          <Plus className="h-4 w-4" />
          <span>Novo evento</span>
        </button>
      </div>

      {/* ── Type stats strip ── */}
      {byType.length > 0 && (
        <div className="flex items-center gap-3 flex-wrap">
          {byType.map(({ type, label, count }) => (
            <div key={type} className="flex items-center gap-2 rounded-xl bg-midnight-2 hairline px-3 py-1.5 text-xs">
              <span className="text-ink-muted">{label}</span>
              <span className="font-bold text-ink">{count}</span>
            </div>
          ))}
        </div>
      )}

      {/* ── Main grid ── */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">

        {/* Calendar panel */}
        <div className="lg:col-span-2 rounded-2xl card-premium p-6">
          {isLoading ? (
            <CalendarSkeleton />
          ) : (
            <EventCalendar
              year={year}
              month={month}
              events={events}
              onPrev={prevMonth}
              onNext={nextMonth}
              onDayClick={handleDayClick}
              onEventClick={(ev) => navigate(`/app/eventos/${ev.id}`)}
            />
          )}
        </div>

        {/* Side panel — day events or upcoming */}
        <div className="rounded-2xl card-premium p-6 space-y-4">
          {selectedDay ? (
            <>
              <div className="flex items-center justify-between">
                <h3 className="font-display text-base font-bold text-ink">
                  {selectedDay.date.toLocaleDateString('pt-BR', {
                    weekday: 'long', day: 'numeric', month: 'long',
                  })}
                </h3>
                <button
                  onClick={() => setSelectedDay(null)}
                  className="rounded-lg p-1.5 text-ink-subtle hover:text-ink hover:bg-white/[0.06] transition-colors"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              {selectedDay.events.length === 0 ? (
                <div className="flex flex-col items-center gap-3 py-8 text-center">
                  <div className="h-10 w-10 rounded-xl bg-midnight-4 flex items-center justify-center">
                    <Calendar className="h-4 w-4 text-ink-subtle" />
                  </div>
                  <p className="text-xs text-ink-muted">Sem eventos nesse dia.</p>
                  <button
                    onClick={() => setShowForm(true)}
                    className="text-xs font-semibold text-accent hover:underline"
                  >
                    Criar evento →
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  {selectedDay.events.map((ev) => (
                    <EventListItem
                      key={ev.id}
                      event={ev}
                      onClick={() => navigate(`/app/eventos/${ev.id}`)}
                    />
                  ))}
                </div>
              )}
            </>
          ) : (
            <UpcomingPanel
              events={events
                .filter((e) => new Date(e.starts_at) >= new Date())
                .slice(0, 5)}
              onEventClick={(ev) => navigate(`/app/eventos/${ev.id}`)}
              onCreateClick={() => setShowForm(true)}
            />
          )}
        </div>
      </div>

      {/* ── Create modal ── */}
      {showForm && (
        <Modal title="Novo evento" onClose={() => setShowForm(false)}>
          <EventForm
            onSubmit={handleCreate}
            onCancel={() => setShowForm(false)}
            isSubmitting={createEvent.isPending}
          />
        </Modal>
      )}
    </div>
  );
}

// ── Upcoming panel ─────────────────────────────────────────────────────────────

interface UpcomingPanelProps {
  events: Event[];
  onEventClick: (ev: Event) => void;
  onCreateClick: () => void;
}

function UpcomingPanel({ events, onEventClick, onCreateClick }: UpcomingPanelProps) {
  return (
    <>
      <div className="flex items-center justify-between">
        <h3 className="font-display text-base font-bold text-ink">Próximos eventos</h3>
      </div>

      {events.length === 0 ? (
        <div className="flex flex-col items-center gap-3 py-8 text-center">
          <div className="h-10 w-10 rounded-xl bg-midnight-4 flex items-center justify-center">
            <Calendar className="h-4 w-4 text-ink-subtle" />
          </div>
          <p className="text-xs text-ink-muted">Nenhum evento agendado.</p>
          <button
            onClick={onCreateClick}
            className="text-xs font-semibold text-accent hover:underline"
          >
            Criar primeiro evento →
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          {events.map((ev) => (
            <EventListItem
              key={ev.id}
              event={ev}
              onClick={() => onEventClick(ev)}
            />
          ))}
        </div>
      )}
    </>
  );
}

// ── Modal wrapper ─────────────────────────────────────────────────────────────

function Modal({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative w-full max-w-2xl rounded-2xl card-premium shadow-deep overflow-y-auto max-h-[90vh]">
        <div className="flex items-center justify-between p-6 border-b border-hairline">
          <h2 className="font-display text-lg font-bold text-ink">{title}</h2>
          <button
            onClick={onClose}
            className="rounded-lg p-2 text-ink-subtle hover:text-ink hover:bg-white/[0.06] transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}

// ── Calendar skeleton ─────────────────────────────────────────────────────────

function CalendarSkeleton() {
  return (
    <div className="space-y-4 animate-pulse">
      <div className="flex items-center justify-between">
        <div className="h-6 w-40 rounded bg-midnight-4" />
        <div className="flex gap-1">
          <div className="h-8 w-8 rounded bg-midnight-4" />
          <div className="h-8 w-8 rounded bg-midnight-4" />
        </div>
      </div>
      <div className="grid grid-cols-7 gap-px">
        {Array.from({ length: 7 }).map((_, i) => (
          <div key={i} className="h-4 rounded bg-midnight-4" />
        ))}
      </div>
      <div className="grid grid-cols-7 gap-px">
        {Array.from({ length: 42 }).map((_, i) => (
          <div key={i} className="h-24 rounded bg-midnight-4/60" />
        ))}
      </div>
    </div>
  );
}
